import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  MiDetailPageHeader,
  MiModalConfirm,
  MiTab,
} from "@miplatform/components";
import { BUTTON_COLORS, ICONS_WEB, MODAL_TITLES } from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { zodResolver } from "@hookform/resolvers/zod";
import * as mdi from "@mdi/js";
import { Footer } from "@mibuildweb/components";
import { AdderDetailForm } from "./AdderDetailForm/AdderDetailForm";
import { useAddAddersViewModel } from "./useAddAddersViewModel";

const schema = z.object({
  name: z.string().min(1, "Name is required"),
  materialIds: z.array(z.string().uuid()).optional(),
});

const tabs = [{ label: "Details", value: "Details" }];

export function AddAdders() {
  const { params, navigate, location } = useRouter();
  const { id: editId } = params;
  const { state } = location;
  const viewOnly = state?.viewOnly ?? true;

  const [activeTab, setActiveTab] = useState("Details");
  const [isReadOnly, setIsReadOnly] = useState(viewOnly);
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [openUpdateModal, setOpenUpdateModal] = useState(false);

  const onBack = () => navigate("/estimating/configuration/adders");
  const onContinue = (id) => {
    navigate(`/estimating/configuration/adders/${id}`, {
      state: { viewOnly: true },
    });
  };

  const {
    formState: { isValid, isDirty },
    control,
    getValues,
    setValue,
    reset,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      name: "",
      materialIds: [],
    },
    resolver: zodResolver(schema),
  });

  const {
    stateManager,
    isLoading,
    adderDetails,
    getAdderDetails,
    handleCreateAdder,
    handleUpdateAdder,
  } = useAddAddersViewModel({ onContinue, reset });

  useEffect(() => {
    if (editId) {
      getAdderDetails(editId);
    }
  }, [editId]);

  const handleSave = async () => {
    if (!editId) {
      const result = await handleCreateAdder(getValues());
      if (result?.id) {
        setIsReadOnly(true);
      }
      return;
    }
    setOpenUpdateModal(true);
  };

  const handleConfirmEdit = async () => {
    setOpenUpdateModal(false);
    const result = await handleUpdateAdder(getValues());
    if (result?.id) {
      setIsReadOnly(true);
    }
  };

  const handleCancel = () => {
    if (!editId) {
      onBack();
      return;
    }
    setIsReadOnly(true);
    reset({ ...adderDetails });
  };

  return (
    <>
      <MiDetailPageHeader
        title={editId ? adderDetails.name : "Add Adder"}
        titleIcon={mdi[ICONS_WEB.CHEVRON_LEFT]}
        loading={isLoading}
        customOnBack={() =>
          editId && !isReadOnly && isDirty ? setOpenCancelModal(true) : onBack()
        }
      />
      <MiTab
        tabs={tabs}
        currentTab={activeTab}
        onTabChange={setActiveTab}
        scrollButtons={false}
        borderBottom
      />
      {activeTab === "Details" && (
        <AdderDetailForm
          isReadOnly={isReadOnly}
          setIsReadOnly={setIsReadOnly}
          editId={editId}
          control={control}
          isLoading={isLoading}
          stateManager={stateManager}
          setValue={setValue}
          getValues={getValues}
        />
      )}
      <Footer
        show={!isReadOnly || !editId}
        saveTitle={editId ? "Update Adder Bucket" : "Save Adder Bucket"}
        handleSaveClick={handleSave}
        handleCancelClick={handleCancel}
        isDirty={isDirty}
        disableSave={isLoading || !isValid || !isDirty}
      />
      <MiModalConfirm
        title={MODAL_TITLES.CANCEL}
        textPositive="Confirm"
        open={openCancelModal}
        handleNegative={() => setOpenCancelModal(false)}
        handlePositive={onBack}
        description="Are you sure you want to cancel editing this adder?"
      />
      <MiModalConfirm
        title={MODAL_TITLES.SAVE}
        textPositive="Yes, Update"
        open={openUpdateModal}
        disableClick={isLoading}
        handleNegative={() => setOpenUpdateModal(false)}
        handlePositive={handleConfirmEdit}
        description={`You are about to save changes to the ${adderDetails?.name} category. Are you sure you would like to continue?`}
        buttonPositiveColor={BUTTON_COLORS.PRIMARY}
      />
    </>
  );
}
